// Public analytics surface. All call sites import from here, never
// from gtag/react-ga4 directly. Design §D *Provider abstraction*.
//
// Usage:
//   import { init, track, identify, setConsent, pageView } from '@lf/analytics-events';
//
//   init({
//     provider: process.env.REACT_APP_ANALYTICS_PROVIDER || 'memory',
//     measurementId: process.env.REACT_APP_GA4_ID,
//     environment: process.env.REACT_APP_LF_ENV || 'local',
//     appRole: 'parent', // 'parent' | 'child' | 'marketing'
//   });
//   track('login', { method: 'email_token' });

import { memoryAdapter } from './adapters/memory.js';
import { offAdapter } from './adapters/off.js';
import { makeGa4Adapter } from './adapters/ga4.js';
import { deriveUserId } from './identity.js';
import { isKnownEventName } from './taxonomy.js';

// ---- module state ---------------------------------------------------

let adapters = [];
let consentState = 'denied'; // 'granted' | 'denied'
let environment = 'local';
let appRole = null;
let currentUserId = null;
let currentUserProps = {};
let pendingQueue = []; // events deferred while consent is denied
let initialized = false;

// ---- helpers --------------------------------------------------------

function resolveAdapters(config) {
    const providers = Array.isArray(config.provider)
        ? config.provider
        : [config.provider || 'memory'];

    return providers
        .filter(Boolean)
        .map((p) => {
            switch (p) {
                case 'memory':
                    return memoryAdapter;
                case 'off':
                    return offAdapter;
                case 'ga4':
                    if (!config.measurementId) {
                        if (typeof console !== 'undefined' && console.warn) {
                            console.warn(
                                '[analytics] provider=ga4 selected but REACT_APP_GA4_ID is unset; falling back to memory adapter.',
                            );
                        }
                        return memoryAdapter;
                    }
                    return makeGa4Adapter({
                        measurementId: config.measurementId,
                        debug: !!config.debug,
                        environment: config.environment || 'prod',
                    });
                default:
                    throw new Error(`[analytics] Unknown provider: ${p}`);
            }
        });
}

function fanoutContext() {
    return {
        userId: currentUserId,
        userProperties: { ...currentUserProps, app_role: appRole },
        environment,
    };
}

function flushPending() {
    if (pendingQueue.length === 0) return;
    const queued = pendingQueue.slice();
    pendingQueue = [];
    queued.forEach((q) => {
        if (q.kind === 'track') {
            doTrack(q.name, q.params);
        } else if (q.kind === 'pageView') {
            doPageView(q.routeId, q.params);
        }
    });
}

function gateOnConsent() {
    // The memory adapter records regardless of consent so tests can
    // assert what *would* have fired. Other adapters are gated.
    return consentState === 'granted';
}

function doTrack(name, params) {
    const enrichedParams = enrichParams(params);
    adapters.forEach((adapter) => {
        if (adapter.name === 'memory') {
            adapter.track(name, enrichedParams, fanoutContext());
            return;
        }
        if (!gateOnConsent()) return;
        adapter.track(name, enrichedParams, fanoutContext());
    });
}

function doPageView(routeId, params) {
    const enrichedParams = enrichParams(params);
    adapters.forEach((adapter) => {
        if (adapter.name === 'memory') {
            adapter.pageView(routeId, enrichedParams, fanoutContext());
            return;
        }
        if (!gateOnConsent()) return;
        adapter.pageView(routeId, enrichedParams, fanoutContext());
    });
}

function enrichParams(params) {
    const out = { ...(params || {}) };
    if (!('environment' in out)) out.environment = environment;
    if (appRole && !('app_role' in out)) out.app_role = appRole;
    return out;
}

// ---- public API -----------------------------------------------------

export function init(config = {}) {
    environment = config.environment || environment;
    appRole = config.appRole || appRole;
    adapters = resolveAdapters(config);
    adapters.forEach((adapter) => {
        try {
            adapter.init(config);
        } catch (err) {
            if (typeof console !== 'undefined' && console.warn) {
                console.warn(`[analytics] adapter ${adapter.name} init failed:`, err);
            }
        }
    });
    initialized = true;
}

export function setAppRole(role) {
    if (role && role !== 'parent' && role !== 'child' && role !== 'marketing') {
        throw new Error(`[analytics] Unknown app_role: ${role}`);
    }
    appRole = role;
}

export function track(name, params = {}) {
    if (!initialized) {
        // Defer until init runs — common in early-boot flows.
        pendingQueue.push({ kind: 'track', name, params });
        return;
    }
    if (!isKnownEventName(name)) {
        if (typeof console !== 'undefined' && console.warn) {
            console.warn(`[analytics] Unknown event name "${name}" — see packages/analytics-events/src/taxonomy.js.`);
        }
    }
    if (consentState === 'denied') {
        // Queue for replay if consent flips to granted.
        pendingQueue.push({ kind: 'track', name, params });
        // Still record into memory adapter so tests see the event.
        adapters.forEach((adapter) => {
            if (adapter.name === 'memory') {
                adapter.track(name, enrichParams(params), fanoutContext());
            }
        });
        return;
    }
    doTrack(name, params);
}

export function pageView(routeId, params = {}) {
    if (!initialized) {
        pendingQueue.push({ kind: 'pageView', routeId, params });
        return;
    }
    if (consentState === 'denied') {
        pendingQueue.push({ kind: 'pageView', routeId, params });
        adapters.forEach((adapter) => {
            if (adapter.name === 'memory') {
                adapter.pageView(routeId, enrichParams(params), fanoutContext());
            }
        });
        return;
    }
    doPageView(routeId, params);
}

export function identify(userId, userProps = {}) {
    currentUserId = userId || null;
    currentUserProps = userProps ? { ...userProps } : {};
    adapters.forEach((adapter) => adapter.identify(userId, userProps));
}

export function setConsent(state) {
    const next = state === 'granted' ? 'granted' : 'denied';
    const wasDenied = consentState === 'denied';
    consentState = next;
    adapters.forEach((adapter) => adapter.setConsent(next));
    if (next === 'granted' && wasDenied) {
        flushPending();
    } else if (next === 'denied') {
        // Drop pending queue — user revoked.
        pendingQueue = [];
    }
}

// Convenience for blueprints that don't want to call deriveUserId
// themselves before identify().
export async function identifyByProfile({ role, profileId, salt, userProperties }) {
    const userId = await deriveUserId(role, profileId, salt);
    identify(userId, userProperties || {});
    return userId;
}

export function getConsentState() {
    return consentState;
}

export function _testReset() {
    // Test-only helper. Resets module state between tests.
    adapters = [];
    consentState = 'denied';
    environment = 'local';
    appRole = null;
    currentUserId = null;
    currentUserProps = {};
    pendingQueue = [];
    initialized = false;
}

// Re-exports so tests and call sites can pull what they need from a
// single import path.
export { memoryAdapter } from './adapters/memory.js';
export { offAdapter } from './adapters/off.js';
export { makeGa4Adapter } from './adapters/ga4.js';
export { deriveUserId, deriveUserIdSync } from './identity.js';
export {
    EVENT_NAMES,
    EVENT_SCHEMA,
    EMAIL_KINDS,
    VIDEO_SOURCES,
    CONVERSION_EVENTS,
    CUSTOM_DIMENSIONS,
    ROUTE_IDS,
    routeIdForPath,
    isKnownEventName,
} from './taxonomy.js';
